<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    elem-id="modal_crud_so-service-priorities"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="isDeleteMode ? (isDeleting ? t('Deletando') : t('Deletar')) : (isSaving ? t('Salvando') : t('Salvar'))"
    :save-fn="isDeleteMode ? handleDelete : handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <!-- Delete Confirmation Mode -->
      <div v-if="isDeleteMode" class="delete-confirmation">
        <p>{{ t('Tem certeza que deseja deletar esta entrada da matriz?') }}</p>
        <p class="text-muted">{{ entryToDelete?.so_service_priority_service_name }} - {{ entryToDelete?.so_service_priority_priority_name }}</p>
      </div>

      <!-- Create/Edit Mode -->
      <div v-else>
        <s-o-service-priorities-form
          ref="formRef"
          :entryId="currentEntryId"
          :isEditMode="isEditMode"
          @submit-success="handleFormSuccess"
          @submit-error="handleFormError"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOServicePriorities } from '../../composables/useSOServicePriorities';
import { eventBus } from '../../eventBus';
import SOServicePrioritiesForm from './SOServicePrioritiesForm.vue';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface EntryData {
  so_service_priority_id?: Id;
  so_service_priority_service_name?: string;
  so_service_priority_priority_name?: string;
  [key: string]: unknown;
}

interface SOServicePrioritiesFormInstance {
  resetForm: () => void;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
}

interface ModalOpenPayload {
  id?: Id;
  data?: EntryData | null;
  action?: 'create' | 'edit' | 'delete';
}

// Get i18n composable
const { t } = useI18n();

// Use composables
const { remove } = useSOServicePriorities();

// Reactive data
const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const isDeleteMode = ref<boolean>(false);
const currentEntryId = ref<Id | null>(null);
const entryToDelete = ref<EntryData | null>(null);
const formRef = ref<SOServicePrioritiesFormInstance | null>(null);
const isSaving = ref<boolean>(false);
const isDeleting = ref<boolean>(false);

// Computed properties
const modalTitle = computed(() => {
  if (isDeleteMode.value) {
    return t('Deletar Matriz Serviço-Prioridade');
  } else if (isEditMode.value) {
    return t('Editar Matriz Serviço-Prioridade');
  } else {
    return t('Adicionar Matriz Serviço-Prioridade');
  }
});

// Methods
const openForCreate = (): void => {
  log('SOServicePrioritiesModal', 'openForCreate', 'Opening modal for create');
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentEntryId.value = null;

  // Reset form after modal is visible
  nextTick(() => {
    if (formRef.value) {
      formRef.value.resetForm();
    }
  });
};

const openForEdit = async (entryId: Id): Promise<void> => {
  log('SOServicePrioritiesModal', 'openForEdit', 'Opening modal for edit', entryId);
  isVisible.value = true;
  isEditMode.value = true;
  isDeleteMode.value = false;
  currentEntryId.value = entryId;

  // Wait for the form component to be rendered
  await nextTick();

  // Explicitly load data for edit
  if (formRef.value) {
    await formRef.value.loadForEdit();
  }
};

const openForDelete = async (entryId: Id, entryData: EntryData | null = null): Promise<void> => {
  log('SOServicePrioritiesModal', 'openForDelete', 'Opening modal for delete', entryId);
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = true;
  currentEntryId.value = entryId;
  entryToDelete.value = entryData;
};

const handleClose = (): void => {
  log('SOServicePrioritiesModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentEntryId.value = null;
  entryToDelete.value = null;
  isSaving.value = false;
  isDeleting.value = false;

  // Reset form
  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  log('SOServicePrioritiesModal', 'handleSave', 'Save button clicked');
  if (formRef.value) {
    isSaving.value = true;
    try {
      await formRef.value.handleSubmit();
    } finally {
      isSaving.value = false;
    }
  }
};

const handleDelete = async (): Promise<void> => {
  if (!currentEntryId.value) {
    logWarn('SOServicePrioritiesModal', 'handleDelete', 'No entry ID to delete');
    return;
  }

  log('SOServicePrioritiesModal', 'handleDelete', 'Deleting entry', currentEntryId.value);
  isDeleting.value = true;

  try {
    await remove({ so_service_priority_id: Number(currentEntryId.value) });
    eventBus.emit('globalMessage.success', t('Entrada da matriz deletada com sucesso'));
    eventBus.emit('so-service-priorities.deleted');
    handleClose();
  } catch (error: unknown) {
    logError('SOServicePrioritiesModal', 'handleDelete', 'Error deleting entry', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao deletar entrada da matriz') + ': ' + message);
  } finally {
    isDeleting.value = false;
  }
};

const handleFormSuccess = (): void => {
  log('SOServicePrioritiesModal', 'handleFormSuccess', 'Form submitted successfully');
  eventBus.emit('so-service-priorities.saved');
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('SOServicePrioritiesModal', 'handleFormError', 'Form submission error', error);
  // Error message is already shown by the form component
};

// Standard modal open handler (for modal.so-service-priorities.open event)
const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('SOServicePrioritiesModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.action === 'delete' && data?.id) {
    await openForDelete(data.id, data.data || null);
  } else if (data?.id) {
    await openForEdit(data.id);
  } else {
    openForCreate();
  }
};

// Lifecycle - Event listeners
onMounted(() => {
  log('SOServicePrioritiesModal', 'onMounted', 'Component mounted, setting up event listeners');

  // Standard modal events (preferred pattern)
  eventBus.on<ModalOpenPayload>('modal.so-service-priorities.open', handleModalOpen);
  eventBus.on('modal.so-service-priorities.close', handleClose);
});

onBeforeUnmount(() => {
  log('SOServicePrioritiesModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');

  // Remove standard modal events
  eventBus.off<ModalOpenPayload>('modal.so-service-priorities.open', handleModalOpen);
  eventBus.off('modal.so-service-priorities.close', handleClose);
});

// Expose methods for parent components (legacy support)
defineExpose({
  openForCreate,
  openForEdit,
  openForDelete
});
</script>

<style scoped>
.delete-confirmation {
  padding: 20px;
  text-align: center;
}

.delete-confirmation p {
  margin-bottom: 10px;
  font-size: 16px;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}
</style>
